import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, ShieldCheck } from 'lucide-react';

/**
 * SystemPayForm - composant React unifie pour l'affichage du formulaire de
 * paiement SystemPay (SDK Krypton).
 *
 * Resout les conflits DOM React <-> Krypton en :
 *  - montant un noeud DOM enfant frais (non controle par React) a chaque token
 *  - nettoyant KR.removeForms() + innerHTML avant chaque init et au unmount
 *  - attendant que window.KR soit pret avant d'appeler addForm()
 *
 * Props:
 *  - formToken (string) : token SystemPay obtenu depuis le backend
 *  - amount (number | null) : montant en EUR a afficher (optionnel)
 *  - onPaid (function) : callback asynchrone appele avec resp.clientAnswer lorsque orderStatus === 'PAID'
 *  - onFailed (function) : callback appele avec resp.clientAnswer quand le paiement est refuse
 *  - testId (string) : data-testid pour le conteneur (optionnel)
 */
export default function SystemPayForm({
  formToken,
  amount = null,
  onPaid,
  onFailed,
  testId = 'systempay-form'
}) {
  const rootRef = useRef(null);
  const initialisedForRef = useRef(null);
  const [status, setStatus] = useState('loading'); // loading | ready | error

  useEffect(() => {
    if (!formToken) return;
    // Eviter double init pour le meme token (StrictMode / re-render React)
    if (initialisedForRef.current === formToken) return;

    let cancelled = false;
    let retry = 0;

    const cleanupContainer = () => {
      if (rootRef.current) {
        rootRef.current.innerHTML = '';
      }
    };

    const mount = async () => {
      if (cancelled) return;
      if (!window.KR) {
        if (retry++ < 50) {
          setTimeout(mount, 200);
          return;
        }
        setStatus('error');
        toast.error('SystemPay non charge. Rechargez la page.');
        return;
      }
      if (!rootRef.current) return;

      try {
        // 1. Nettoyer les anciens formulaires Krypton (autres pages / re-init)
        try { if (window.KR.removeForms) await window.KR.removeForms(); } catch (e) { /* ignore */ }

        // 2. Vider completement notre conteneur (supprime toute trace DOM precedente)
        cleanupContainer();
        if (cancelled) return;

        // 3. Creer un noeud DOM frais (NON gere par React) pour le smart-form
        const freshId = `kr-sf-${Date.now()}-${Math.floor(Math.random() * 1e6)}`;
        const sf = document.createElement('div');
        sf.id = freshId;
        sf.className = 'kr-smart-form';
        sf.setAttribute('kr-card-form-expanded', '');
        sf.setAttribute('kr-form-token', formToken);
        // Affiche Apple Pay / Google Pay en plus de CB si actives au BO Lyra
        // Si non actives, le SDK les ignore silencieusement (aucun risque)
        sf.setAttribute('kr-payment-method-shown', 'CB,VISA,MASTERCARD,APPLE_PAY,GOOGLE_PAY,VISA_ELECTRON,MAESTRO');
        rootRef.current.appendChild(sf);

        // 4. Configurer KR pour ce token
        await window.KR.setFormConfig({ formToken, 'kr-language': 'fr-FR' });

        // 5. Enregistrer les handlers AVANT addForm
        window.KR.onSubmit(async (resp) => {
          const answer = resp.clientAnswer || {};
          if (answer.orderStatus === 'PAID') {
            try { if (onPaid) await onPaid(answer, resp); } catch (e) { console.error('onPaid error', e); }
          } else {
            try { if (onFailed) await onFailed(answer, resp); } catch (e) { console.error('onFailed error', e); }
          }
          return false; // empeche la redirection par defaut
        });

        window.KR.onError((event) => {
          const msg = event?.errorMessage || event?.detailedErrorMessage || 'Erreur de paiement';
          console.error('[SystemPay] onError:', event);
          toast.error(msg);
        });

        // 6. Attacher le form au noeud frais
        await window.KR.addForm('#' + freshId);

        if (!cancelled) {
          initialisedForRef.current = formToken;
          setStatus('ready');
        }
      } catch (e) {
        console.error('[SystemPay] init error:', e);
        if (!cancelled) setStatus('error');
        toast.error('Erreur chargement du formulaire de paiement');
      }
    };

    setStatus('loading');
    mount();

    return () => {
      cancelled = true;
      try { if (window.KR && window.KR.removeForms) window.KR.removeForms(); } catch (e) { /* ignore */ }
      cleanupContainer();
      initialisedForRef.current = null;
    };
  }, [formToken, onPaid, onFailed]);

  return (
    <div className="space-y-4" data-testid={testId}>
      {amount != null && (
        <div className="text-center py-4 bg-zinc-800/50 rounded-lg border border-zinc-700">
          <p className="text-zinc-400 text-sm">Montant a payer</p>
          <p className="text-3xl font-bold text-white" data-testid="systempay-amount">
            {Number(amount).toFixed(2)} EUR
          </p>
        </div>
      )}

      <div className="flex items-center justify-center gap-2 text-zinc-400 text-sm">
        <ShieldCheck size={16} className="text-green-500" />
        <span>Paiement securise par SystemPay</span>
      </div>

      {status === 'loading' && (
        <div className="text-center py-8" data-testid="systempay-loading">
          <Loader2 className="mx-auto animate-spin text-red-500 mb-4" size={32} />
          <p className="text-zinc-400">Chargement du formulaire de paiement...</p>
        </div>
      )}

      {status === 'error' && (
        <div className="text-center py-4 text-red-400 text-sm" data-testid="systempay-error">
          Le formulaire n'a pas pu se charger. Merci de rafraichir la page.
        </div>
      )}

      {/* Conteneur React : KR cree un noeud enfant dedans */}
      <div ref={rootRef} data-testid="systempay-root" />
    </div>
  );
}
